import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { keyframes } from "@emotion/react";
import esn_copenhagen from "src/media/partners/esn-copenhagen.png";
import esn_rotterdam from "src/media/partners/esn-rotterdam.png";
import esn_tromso from "src/media/partners/esn-tromso.png";
import blekinge_student_union from "src/media/partners/blekinge-student-union.png";

type Partner = {
  name: string;
  logo: string;
  url?: string;
};

const partners: Partner[] = [
  {
    name: "Blekinge Student Union",
    logo: blekinge_student_union,
    url: "./press-releases/blekinge-student-union",
  },
  {
    name: "ESN Copenhagen",
    logo: esn_copenhagen,
    url: "./press-releases/esn-copenhagen",
  },
  {
    name: "ESN Rotterdam",
    logo: esn_rotterdam,
    url: "./press-releases/esn-rotterdam",
  },
  {
    name: "ESN Tromso",
    logo: esn_tromso,
    url: "./press-releases/esn-tromso",
  },
];

const slideLeftToRight = keyframes`
  0% { left: calc(-1 * (var(--item-width) + var(--lane-gap))); }
  100% { left: calc(100% + var(--lane-gap)); }
`;

export default function PartnersCarousel() {
  const partnerCount = partners.length;
  const secondsPerPartner = 6;
  const totalDurationSeconds = Math.max(12, partnerCount * secondsPerPartner);

  return (
    <Box sx={{ width: "100%", pl: 5, pt: 5 }}>
      <Typography variant="h2">{"Our partners"}</Typography>
      <Box
        sx={{
          position: "relative",
          py: 3,
          minHeight: { xs: 270, sm: 250, md: 240 },
          width: "100%",
          maxWidth: { xs: 320, sm: 420, md: "100%" },
          mx: "auto",
          overflow: "hidden",
          "--item-width": "clamp(190px, 22vw, 260px)",
          "--lane-gap": "calc(var(--item-width) * 0.45)",
        }}
      >
        {partners.map((partner, index) => {
          const isLinked = Boolean(partner.url);
          const phaseDelaySeconds = (index * totalDurationSeconds) / partnerCount;

          return (
            <Box
              key={partner.name}
              component={isLinked ? "a" : "div"}
              href={isLinked ? partner.url : undefined}
              target={isLinked ? "_blank" : undefined}
              rel={isLinked ? "noreferrer" : undefined}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
                px: 3,
                py: 3,
                width: "var(--item-width)",
                position: "absolute",
                top: 0,
                left: "calc(-1 * (var(--item-width) + var(--lane-gap)))",
                animation: `${slideLeftToRight} ${totalDurationSeconds}s linear infinite`,
                animationDelay: `${-phaseDelaySeconds}s`,
                animationFillMode: "both",
                textDecoration: "none",
                color: "inherit",
                cursor: isLinked ? "pointer" : "default",
                textAlign: "center",
                ...(isLinked
                  ? {
                      "&:hover .partner-name": { textDecoration: "underline" },
                      "&:focus-visible": {
                        outline: "2px solid",
                        outlineColor: "currentColor",
                        outlineOffset: 4,
                      },
                    }
                  : {}),
              }}
            >
              <Box
                component="img"
                src={partner.logo}
                alt={`${partner.name} logo`}
                sx={{
                  width: { xs: 120, sm: 160, md: 180 },
                  height: { xs: 120, sm: 160, md: 180 },
                  objectFit: "contain",
                }}
              />
              <Typography
                variant="subtitle1"
                className="partner-name"
                sx={{ fontWeight: 600, letterSpacing: "0.02em" }}
              >
                {partner.name}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
